import React from 'react';
import { Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import apiService from '../services/api';
import { getApiOrigin } from '../config/environment';

type Props = {
  label: string;
  value: string;
  onChange: (url: string) => void;
};

function resolveIconUrl(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `${getApiOrigin()}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`;
}

const SplashIconUploadField: React.FC<Props> = ({ label, value, onChange }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.svg')) {
      setError('Seuls les fichiers SVG sont acceptés.');
      return;
    }

    setError(null);
    setUploading(true);
    try {
      const response = await apiService.uploadHomeSplashScreenIcon(file);
      if (response.success && response.icon?.url) {
        onChange(response.icon.url);
      } else {
        setError('Erreur lors de l\'upload.');
      }
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(message || 'Erreur lors de l\'upload.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {value ? (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#f8fafc',
            }}
          >
            <Box component="img" src={resolveIconUrl(value)} alt="" sx={{ width: 24, height: 24, objectFit: 'contain' }} />
          </Box>
        ) : null}
        <Button
          variant="outlined"
          size="small"
          startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {value ? 'Remplacer le SVG' : 'Uploader un SVG'}
        </Button>
        {value ? (
          <IconButton size="small" aria-label="Supprimer l'icône" onClick={() => onChange('')}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
      {error ? (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      ) : null}
      <input ref={inputRef} type="file" accept=".svg,image/svg+xml" hidden onChange={handleUpload} />
    </Box>
  );
};

export default SplashIconUploadField;
